import { AppBar, Box, Button, Toolbar, Typography, CircularProgress } from '@mui/material';
import { ReactNode, useState, useEffect } from 'react';
import { loginRequest } from '../authConfig';
import { useNavigate } from 'react-router-dom';
import NashLogo from '../assets/Nash_Logo_Final_800.png';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import { getCurrentUser, User } from '../services/userService';

interface LayoutProps {
  children: ReactNode;
}

const LoginButton = () => {
  const { instance } = useMsal();
  
  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <Button color="inherit" onClick={handleLogin}>
      Sign In
    </Button>
  );
};

const LogoutButton = () => {
  const { instance } = useMsal();
  
  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <Button color="inherit" onClick={handleLogout}>
      Sign Out
    </Button>
  );
};

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      } finally {
        setLoading(false);
      }
    };

    // Check if user is authenticated
    if (accounts && accounts.length > 0) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [accounts]);

  // Listen for login events
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        
        // Add a small delay to ensure token is properly set
        setTimeout(async () => {
          try {
            setLoading(true);
            const user = await getCurrentUser();
            setCurrentUser(user);
          } catch (error) {
            console.error('Failed to fetch user after login:', error);
          } finally {
            setLoading(false);
          }
        }, 500); // 500ms delay
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const isAdmin = currentUser?.role?.name === 'Admin';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white' }}>
        <Toolbar sx={{ display: 'flex', gap: 2 }}>
          <img 
            src={NashLogo} 
            alt="NASH" 
            style={{ 
              height: '40px',
              width: 'auto',
              marginLeft: '-16px', // Offset default Toolbar padding
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          />
          <Box sx={{ display: 'flex', gap: 2, ml: 4, flexGrow: 1 }}>
            <AuthenticatedTemplate>
              <Button color="inherit" onClick={() => navigate('/')}>
                Dashboard
              </Button>
              <Button color="inherit" onClick={() => navigate('/transactions')}>
                Transactions
              </Button>
              <Button color="inherit" onClick={() => navigate('/eligibility')}>
                Eligibility
              </Button>
              <Button color="inherit" onClick={() => navigate('/files')}>
                Files
              </Button>
              <Button color="inherit" onClick={() => navigate('/output-logs')}>
                Logs
              </Button>
              <Button color="inherit" onClick={() => navigate('/pbm')}>
                PBMs
              </Button>
              {loading ? (
                <CircularProgress size={20} color="inherit" sx={{ ml: 2 }} />
              ) : isAdmin && (
                <Button color="inherit" onClick={() => navigate('/users')}>
                  User Management
                </Button>
              )}
            </AuthenticatedTemplate>
          </Box>
          <AuthenticatedTemplate>
            <LogoutButton />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginButton />
          </UnauthenticatedTemplate>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flexGrow: 1 }}>
        <AuthenticatedTemplate>
          {children}
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <Typography variant="h6" align="center">Please sign-in to see your profile information.</Typography>
        </UnauthenticatedTemplate>
      </Box>
    </Box>
  );
};

export default Layout;
